import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { accessToken } from "@/stores/auth";
import { ensureResponseOk, fetch } from "@/utils/fetch";
import type { UserCreateInput, UserOperation } from "../types";

const BASE = "/_atmosms/admin/v1/users";

async function request(
  client: QueryClient,
  root: string,
  path: string,
  method: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<UserOperation | null> {
  const response = await fetch(new URL(BASE + path, root), {
    method,
    signal,
    headers: {
      Authorization: `Bearer ${await accessToken(client, signal)}`,
      "Content-Type": "application/json",
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (method === "GET" && response.status === 404) return null;
  const data = await response.json();
  if (!response.ok && typeof data.error === "string")
    throw new Error(data.error);
  ensureResponseOk(response);
  return data as UserOperation;
}

export const operationQuery = (root: string, operation: string) =>
  queryOptions({
    queryKey: ["atmosms", "user-operation", root, operation],
    queryFn: ({ client, signal }) =>
      request(
        client,
        root,
        "/operations/" + operation,
        "GET",
        undefined,
        signal,
      ),
    staleTime: 0,
    refetchInterval: (query) =>
      query.state.data?.stage === "bitrix" ? 5000 : false,
  });

export async function createNamedUser(
  client: QueryClient,
  root: string,
  operation: string,
  input: UserCreateInput,
) {
  const result = await request(client, root, "", "POST", {
    ...input,
    operation_id: operation,
  });
  if (!result) throw new Error("Empty response");
  return result;
}

export async function editUserName(
  client: QueryClient,
  root: string,
  userId: string,
  displayname: string,
) {
  const result = await request(
    client,
    root,
    "/" + encodeURIComponent(userId),
    "PATCH",
    { displayname },
  );
  if (!result) throw new Error("Empty response");
  return result;
}

export async function invalidateUserProfile(
  client: QueryClient,
  root: string,
  serverName: string,
  result: UserOperation,
) {
  await Promise.all([
    client.invalidateQueries({
      queryKey: ["matrix", "profile", root, result.matrix_id],
    }),
    client.invalidateQueries({ queryKey: ["mas", "users", serverName] }),
    client.invalidateQueries({ queryKey: ["mas", "users-count", serverName] }),
  ]);
}
